// Charts.jsx
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { motion } from 'framer-motion';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Charts = ({ data }) => {
  const defaultData = {
    ticketTrends: {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      datasets: [
        {
          label: 'Created',
          data: [12, 19, 15, 25, 22, 30],
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Resolved',
          data: [8, 15, 12, 20, 18, 28],
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    },
    statusDistribution: {
      labels: ['Open', 'In Progress', 'Resolved', 'Closed'],
      datasets: [
        {
          data: [30, 25, 35, 10],
          backgroundColor: [
            'rgba(249, 115, 22, 0.8)',
            'rgba(59, 130, 246, 0.8)',
            'rgba(16, 185, 129, 0.8)',
            'rgba(107, 114, 128, 0.8)'
          ],
          borderColor: [
            'rgba(249, 115, 22, 1)',
            'rgba(59, 130, 246, 1)',
            'rgba(16, 185, 129, 1)',
            'rgba(107, 114, 128, 1)'
          ],
          borderWidth: 1
        }
      ]
    },
    branchPerformance: {
      labels: ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Kolkata'],
      datasets: [
        {
          label: 'Tickets Resolved',
          data: [65, 59, 80, 81, 56],
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false
        }
      ]
    }
  };

  const chartData = data || defaultData;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            family: 'Inter, sans-serif'
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        padding: 12
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { size: 11, family: 'Inter, sans-serif' },
          color: '#6b7280'
        }
      },
      y: {
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: {
          font: { size: 11, family: 'Inter, sans-serif' },
          color: '#6b7280'
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            family: 'Inter, sans-serif'
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        padding: 12
      }
    },
    cutout: '60%'
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.18 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32 } }
  };

  // Inline styles matching the app's card language
  const styles = {
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '1rem'
    },
    gridLgTwo: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '1rem'
    },
    panel: {
      backgroundColor: '#fff',
      borderRadius: '1rem',
      padding: '1rem',
      border: '1px solid #eef2f6',
      boxShadow: '0 10px 30px rgba(2,6,23,0.04)'
    },
    panelHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '0.75rem'
    },
    title: {
      margin: 0,
      fontSize: '1.125rem',
      fontWeight: 700,
      color: 'var(--gray-900)'
    },
    subtitle: {
      margin: 0,
      fontSize: '0.875rem',
      color: 'var(--gray-600)'
    },
    select: {
      fontSize: '0.875rem',
      border: '1px solid #e6eef3',
      borderRadius: '0.5rem',
      padding: '0.375rem 0.75rem',
      outline: 'none',
      background: 'white',
      cursor: 'pointer'
    },
    chartBox: {
      height: '16rem' // 256px
    },
    chartBoxTall: {
      height: '18rem' // 288px
    }
  };

  return (
    <motion.div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '1rem'
      }}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Ticket Trends (span 2 on large screens) */}
      <motion.div
        style={styles.panel}
        variants={itemVariants}
        whileHover={{ translateY: -4 }}
        transition={{ duration: 0.18 }}
      >
        <div style={styles.panelHeader}>
          <div>
            <h3 style={styles.title}>Ticket Trends</h3>
            <p style={styles.subtitle}>Monthly ticket creation and resolution</p>
          </div>
          <div>
            <select style={styles.select} defaultValue="6">
              <option value="6">Last 6 months</option>
              <option value="12">Last 12 months</option>
              <option value="year">This year</option>
            </select>
          </div>
        </div>

        <div style={styles.chartBox}>
          <Line data={chartData.ticketTrends} options={chartOptions} />
        </div>
      </motion.div>

      {/* Status Distribution */}
      <motion.div
        style={styles.panel}
        variants={itemVariants}
        whileHover={{ translateY: -4 }}
        transition={{ duration: 0.18 }}
      >
        <div style={{ marginBottom: '0.75rem' }}>
          <h3 style={styles.title}>Status Distribution</h3>
          <p style={styles.subtitle}>Current ticket status breakdown</p>
        </div>

        <div style={styles.chartBox}>
          <Doughnut data={chartData.statusDistribution} options={doughnutOptions} />
        </div>
      </motion.div>

      {/* Branch Performance (full width panel) */}
      <motion.div
        style={{ ...styles.panel, gridColumn: '1 / -1' }}
        variants={itemVariants}
        whileHover={{ translateY: -4 }}
        transition={{ duration: 0.18 }}
      >
        <div style={styles.panelHeader}>
          <div>
            <h3 style={styles.title}>Branch Performance</h3>
            <p style={styles.subtitle}>Tickets resolved by branch this month</p>
          </div>
          <div>
            <select style={styles.select} defaultValue="this">
              <option value="this">This month</option>
              <option value="last">Last month</option>
              <option value="3">Last 3 months</option>
            </select>
          </div>
        </div>

        <div style={styles.chartBoxTall}>
          <Bar data={chartData.branchPerformance} options={chartOptions} />
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Charts;
